<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(defineProps<{
  fund: 'BGA' | 'JH' | 'JAPAN' | 'CHARTERIS' | null
  decisionLabel: string
  decision: 'buy' | 'sell' | 'hold' | null
  decisionSubtitle: string
  synthNavPence: number | null
  overnightReturnPct: number | null
  anchorPence: number | null
  anchorDateLabel: string | null
  countdown: string
  noTrade: { reason: string; weekend: boolean; nextTradingDay: string | null } | null
  xdBanner: { plusOne: boolean; active: boolean } | null
  rawSignal: number | null
  // Needle magnitude scale (tanh denominator). 0.015 suits the synth-NAV band
  // signal; the futures rule passes 0.005 for the NQ overnight move.
  needleScale?: number
  // Pure step-aside pre-08:40: the print is still drifting, the verdict is
  // provisional and rendered muted so an 08:20 BUY is not read as final.
  provisional?: boolean
  statePill?: { state: 'live' | 'frozen' | 'degraded'; label: string } | null
  // Japan fund page UI Task 12: Japan has no synth NAV — JapanSignalTab
  // passes false to drop the NAV hero. Defaults to true (every other fund).
  showNavBlock?: boolean
  // Label on the countdown box. Japan passes 'Decision' (its countdown is
  // to the 08:40 decision, not an HL dealing cutoff).
  cutoffLabel?: string
}>(), { showNavBlock: true, cutoffLabel: 'HL Cutoff' })
defineEmits<{ explainNav: [] }>()

// --- Binary semicircle gauge (re-instated 2026-07-28) ---
// needleAngle maps raw signal strength; gaugeAngle reflects the DECISION,
// not the raw signal — when momentum blocks a positive signal the needle
// points SELL. Hold (and no decision) leaves the needle vertical.
const needleAngle = computed(() => {
  if (props.rawSignal == null) return 0
  return Math.tanh(props.rawSignal / (props.needleScale ?? 0.015)) * 80
})

const gaugeAngle = computed(() => {
  if (props.decision == null || props.decision === 'hold') return 0
  const magnitude = Math.abs(needleAngle.value)
  // Minimum deflection of 30° so needle is clearly on one side
  const deflection = Math.max(magnitude, 30)
  return props.decision === 'buy' ? deflection : -deflection
})
</script>

<template>
  <div class="signal-verdict">
    <!-- Ex-dividend day prominent banner. XD-day = trading blackout +
         synth-NAV undershoot; XD+1 = NAV correction only, trading unblocked. -->
    <div v-if="xdBanner" class="xd-banner" :class="xdBanner.active ? 'xd-banner-active' : 'xd-banner-inactive'">
      <div class="xd-banner-title">{{ xdBanner.plusOne ? 'EX-DIVIDEND +1 DAY' : 'EX-DIVIDEND DAY' }}</div>
      <div class="xd-banner-reason">
        <template v-if="xdBanner.plusOne">
          Synth NAV expected to overshoot official by ~160 bps &mdash; correction layer applies. Trading unblocked.
        </template>
        <template v-else-if="xdBanner.active">
          New trade decisions blocked &mdash; synth NAV expected to misprice official by ~200 bps.
        </template>
        <template v-else>
          Blackout filter disabled &mdash; signals may be spurious (~200 bps expected misprice).
        </template>
      </div>
    </div>

    <div class="top-bar">
      <!-- No-trade day banner replaces the decision block entirely. -->
      <div v-if="noTrade" class="no-trade-banner" :class="noTrade.weekend ? 'banner-weekend' : 'banner-holiday'">
        <div class="no-trade-title">NO TRADE DAY</div>
        <div class="no-trade-reason">{{ noTrade.reason }}</div>
        <div v-if="noTrade.nextTradingDay" class="no-trade-resume">Markets resume {{ noTrade.nextTradingDay }}</div>
      </div>

      <!-- Binary BUY/SELL gauge — hidden on no-trade days along with the
           decision block it annotates. -->
      <div v-if="!noTrade" class="gauge-wrap">
        <svg viewBox="0 0 100 68" class="binary-gauge" :class="{ 'gauge-provisional': provisional }">
          <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke="#21262d" stroke-width="10" stroke-linecap="round"/>
          <path d="M 10 50 A 40 40 0 0 1 50 10" fill="none" stroke="#f85149" stroke-width="10" stroke-linecap="round"/>
          <path d="M 50 10 A 40 40 0 0 1 90 50" fill="none" stroke="#3fb950" stroke-width="10" stroke-linecap="round"/>
          <line x1="50" y1="12" x2="50" y2="18" stroke="#484f58" stroke-width="1.5"/>
          <!-- Needle: points based on decision, not raw signal -->
          <line x1="50" y1="50" data-testid="gauge-needle"
            :x2="50 + 38 * Math.sin(gaugeAngle * Math.PI / 180)"
            :y2="50 - 38 * Math.cos(gaugeAngle * Math.PI / 180)"
            stroke="#e6edf3" stroke-width="2.5" stroke-linecap="round"/>
          <circle cx="50" cy="50" r="4" fill="#e6edf3"/>
          <text x="6" y="65" fill="#f85149" font-size="7" font-family="system-ui" font-weight="600" text-anchor="start">SELL</text>
          <text x="94" y="65" fill="#3fb950" font-size="7" font-family="system-ui" font-weight="600" text-anchor="end">BUY</text>
        </svg>
      </div>

      <!-- Decision text. `decision === null` (no signal payload) leaves the
           class list neutral — before the fix a null decision inherited the
           `dt-sell` colour and lit the placeholder up red. -->
      <div v-if="!noTrade" class="decision-block">
        <div class="decision-text" :class="[decision === 'hold' ? 'dt-hold' : decision === 'buy' ? 'dt-buy' : decision === 'sell' ? 'dt-sell' : '', { 'dt-provisional': provisional }]">{{ decisionLabel }}</div>
        <div class="decision-subtitle">
          {{ decisionSubtitle }}
          <span v-if="statePill" class="state-pill" :class="'pill-' + statePill.state" data-testid="step-aside-pill">{{ statePill.label }}</span>
        </div>
      </div>

      <!-- NAV hero — fund-aware. -->
      <div v-if="showNavBlock" class="top-nav" :data-testid="fund === 'JH' ? 'jh-nav-hero' : 'bga-nav-hero'">
        <div class="nav-label">
          {{ fund === 'JH' ? 'Synth JH NAV' : 'Synth BGA NAV' }}
          <span class="info-icon" @click.stop="$emit('explainNav')">i</span>
        </div>
        <div class="nav-value">{{ synthNavPence != null ? synthNavPence.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + 'p' : '—' }}</div>
        <div class="nav-returns">
          <div class="nav-return" :class="overnightReturnPct != null && overnightReturnPct >= 0 ? 'ret-pos' : 'ret-neg'">
            {{ overnightReturnPct != null ? (overnightReturnPct >= 0 ? '▲ +' : '▼ ') + Math.abs(overnightReturnPct).toFixed(2) + '%' : '—' }}
          </div>
        </div>
        <div class="nav-anchor" v-if="anchorPence != null || anchorDateLabel">
          anchor {{ anchorPence != null ? Math.round(anchorPence) + 'p' : '--' }}{{ anchorDateLabel ? ' (' + anchorDateLabel + ')' : '' }}
        </div>
      </div>

      <!-- Cutoff timer -->
      <div class="top-cutoff">
        <div class="cutoff-label">{{ cutoffLabel }}</div>
        <div class="cutoff-time" :class="countdown === 'PASSED' ? 'cutoff-passed' : 'cutoff-active'">{{ countdown }}</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.no-trade-banner {
  padding: 10px 12px;
  border-radius: 4px;
  margin-bottom: 8px;
  text-align: center;
  flex: 1;
}
.banner-holiday { background: #2a1a0a; border: 1px solid #ff9800; }
.banner-weekend { background: #161b22; border: 1px solid #21262d; }
.no-trade-title { font-size: 1.3rem; font-weight: 700; letter-spacing: 2px; color: #ff9800; }
.banner-weekend .no-trade-title { color: #6e7681; }
.no-trade-reason { font-size: 1.25rem; color: #c9d1d9; margin-top: 2px; }
.banner-weekend .no-trade-reason { color: #6e7681; }
.no-trade-resume { font-size: 1.1rem; color: #6e7681; margin-top: 2px; }

.xd-banner {
  padding: 10px 12px;
  border-radius: 4px;
  margin-bottom: 8px;
  text-align: center;
}
.xd-banner-active { background: #2a1a0a; border: 1px solid #ff9800; }
.xd-banner-inactive { background: #2a0a0a; border: 1px solid #f44336; }
.xd-banner-title { font-size: 1.3rem; font-weight: 700; letter-spacing: 2px; }
.xd-banner-active .xd-banner-title { color: #ff9800; }
.xd-banner-inactive .xd-banner-title { color: #f44336; }
.xd-banner-reason { font-size: 1rem; color: #c9d1d9; margin-top: 4px; }

/* ===== TOP BAR ===== */
.top-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #21262d;
  margin-bottom: 12px;
}

.gauge-wrap { flex: 0 0 auto; }
.binary-gauge { width: 82px; height: 56px; display: block; }
@media (max-width: 700px) {
  .binary-gauge { width: 66px; height: 45px; }
}

.decision-block { flex: 1; }
.decision-text { font-size: 2.35rem; font-weight: 700; line-height: 1; }
.dt-buy { color: #3fb950; }
.dt-sell { color: #f85149; }
.dt-hold { color: #d29922; }
.decision-subtitle { font-size: 0.94rem; color: #8b949e; }
/* Pure step-aside pre-08:40: verdict provisional until the print freezes. */
.dt-provisional { opacity: 0.6; text-decoration: underline dashed; text-underline-offset: 6px; }
.gauge-provisional { opacity: 0.55; }
.state-pill { font-size: 0.65rem; padding: 0 7px; border-radius: 9px; margin-left: 6px; vertical-align: middle;
  border: 1px solid #30363d; text-transform: none; white-space: nowrap; }
.pill-live { color: #3fb950; border-color: #238636; background: #23863622; }
.pill-frozen { color: #58a6ff; border-color: #1f6feb; background: #1f6feb22; }
.pill-degraded { color: #d29922; border-color: #9e6a03; background: #9e6a0322; }

.top-nav { text-align: right; }
.nav-label { font-size: 0.7rem; color: #6e7681; text-transform: uppercase; }
.nav-value { font-size: 1.875rem; font-weight: 700; color: #e6edf3; line-height: 1.1; }
.nav-returns { text-align: right; }
.nav-return { font-size: 1.0rem; }
.ret-pos { color: #3fb950; }
.ret-neg { color: #f85149; }
.nav-anchor { font-size: 0.7rem; color: #6e7681; margin-top: 2px; }

.top-cutoff {
  text-align: right;
  padding-left: 10px;
  border-left: 1px solid #21262d;
}
.cutoff-label { font-size: 0.7rem; color: #6e7681; text-transform: uppercase; letter-spacing: 1px; }
.cutoff-time { font-size: 1.5rem; font-weight: 700; font-family: 'JetBrains Mono', monospace; }
.cutoff-active { color: #c9d1d9; }
.cutoff-passed { color: #f85149; }

.info-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 14px; height: 14px; border-radius: 50%;
  background: #21262d; color: #8b949e; font-size: 0.55rem;
  cursor: pointer; font-style: italic; font-weight: 600;
  margin-left: 4px; vertical-align: middle;
}
.info-icon:hover { background: #30363d; color: #c9d1d9; }
</style>
